import {
  AdjustmentsHorizontalIcon,
  ClockIcon,
  ShieldCheckIcon
} from '@heroicons/react/24/outline'
import PropTypes from 'prop-types'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { getAppSettings } from '../../constants/app-settings'
import trackEvents from '../../constants/track-events'
import { useAivyContext, useLanguageContext } from '../../context'
import { convertNumberToTime } from '../../helpers'
import { trackEvent } from '../../helpers/analytics'
import {
  getCulturalFitMode,
  getDifficultyOptions,
  mapChallengesToCoveredDimensions,
  testBatteryConfig
} from '../../helpers/diagnostic'
import { useCreateCareerNavigation } from '../../hooks/use-create-career-navigation'
import { useMount } from '../../hooks/use-mount'
import { useSetState } from '../../hooks/use-set-state'
import Button from '../tailwind/Button'
import CreateCareerFooter from './CreateCareerFooter'
import DividerWithText from './adjustment/divider-with-text'
import ChallengeCard from './pieces/challenge-card'
import CompactStats from './pieces/compact-stats'

export const I18N_PREFIX = 'create_career.confirm_testbattery.'

const ConfirmTestbattery = ({ career, careerMutation }) => {
  const { t } = useTranslation()
  const { system } = useAivyContext()
  const { language } = useLanguageContext()

  const career_analyse = JSON.parse(career.career_analyse)
  const system_dimensions_data = JSON.parse(system.dimensions_pdf_reports.data)

  const {
    isLoadingBack,
    isLoadingNext,
    handleBackNavigation,
    handleNextNavigation
  } = useCreateCareerNavigation({ career, careerMutation })

  const configuration = testBatteryConfig(
    career_analyse.scores,
    getCulturalFitMode(career.app_settings)
  )

  // filter auf system.challenges: fehlt ein Eintrag im Backend-System-Item
  // CHALLENGES (z.B. CULTURE in einer nicht geseedeten Stage), riss der
  // ungepruefte Zugriff in getDuration und in der ChallengeCard ueber die
  // globale ErrorBoundary das ganze Dashboard mit. Der Long-Modus ist
  // deswegen zusaetzlich im Culture-Fit-Step gesperrt (cultural-fit.js) -
  // hier geht es um Bestandsstellen, die das Gate nicht mehr erreichen.
  const [testbattery] = useState(
    Object.keys(configuration)
      .filter((key) => configuration[key].include === true)
      .filter((exam_id) => !!system.challenges[exam_id])
  )

  const app_settings_challenges = JSON.parse(
    career.app_settings?.challenges || '[]'
  ).map(({ exam_id }) => exam_id)

  // inside this success screen
  // we want only the optional challenges to be additionally selectable
  // moreChallenges = { [exam_id]: true/false } / useSetState!
  const [moreChallenges, setMoreChallenges] = useSetState(
    Object.keys(configuration)
      .filter((key) => configuration[key].optional === true)
      .filter((exam_id) => !testbattery.includes(exam_id))
      .filter((exam_id) => !!system.challenges[exam_id])
      .reduce(
        (object, exam_id) => ({
          ...object,
          [exam_id]: app_settings_challenges.includes(exam_id)
            ? 'selected'
            : 'not_selected'
        }),
        {}
      )
  )

  const [isLoadingExpertMode, setIsLoadingExpertMode] = useState(false)

  useMount(() => {
    trackEvent(trackEvents.CREATE_CAREER_STEP_4_VIEWED, { mode: 'aivy' })
  })

  const getDuration = () => {
    const selected = Object.keys(moreChallenges).filter(
      (key) => moreChallenges[key] === 'selected'
    )

    const timeToComplete = [...testbattery, ...selected]
      .map((exam_id) => system.challenges[exam_id].timeToComplete)
      .reduce((previousValue, currentValue) => previousValue + currentValue, 0)

    const { minutes: duration } = convertNumberToTime(timeToComplete / 60000)

    return { duration }
  }

  const getSelectionDifficulty = () => {
    const selection_difficulty = career_analyse.selection_difficulty ?? 'NORMAL'

    const option = getDifficultyOptions().find(
      ({ difficulty }) => difficulty === selection_difficulty
    )

    return t(option.label)
  }

  const mandatoryCriterions = Object.entries(career_analyse.scores)
    .filter(([_, value]) => value.mandatoryCriterion === true)
    .map(([key]) => key)

  const getMandatoryCriterions = () => {
    const { dimensions_pdf_reports: dimensions } = system

    return mandatoryCriterions
      .map((dimension_key) => dimensions[dimension_key].property[language])
      .join(', ')
  }

  const stats = [
    {
      name: t(I18N_PREFIX + 'duration_title'),
      stat: t(I18N_PREFIX + 'duration_value', getDuration()),
      icon: ClockIcon
    },
    {
      name: t(I18N_PREFIX + 'selection_difficulty_label'),
      stat: getSelectionDifficulty(),
      icon: AdjustmentsHorizontalIcon
    },
    !!mandatoryCriterions.length && {
      name: t(I18N_PREFIX + 'must_criteria_label'),
      stat: getMandatoryCriterions(),
      icon: ShieldCheckIcon
    }
  ].filter((item) => !!item)

  const handleNext = ({ status }) => {
    const { currentChallengeConfigId } = system

    const {
      career_analyse,
      challenge_config_id,
      app_settings,
      customer_dashboard_meta
    } = career

    const challenges = [
      ...testbattery.map((exam_id) => ({ exam_id })),
      ...Object.entries(moreChallenges)
        .filter(([_, value]) => value === 'selected')
        .map(([key]) => ({ exam_id: key }))
    ]

    const dimensions = mapChallengesToCoveredDimensions({
      challenges,
      dimensions: Object.values(system_dimensions_data)
    })

    const meta = JSON.parse(customer_dashboard_meta || '{}')
    const parsed_career_analyse = { ...JSON.parse(career_analyse) }

    const updatedOverrideDefaults = {
      selected_challenges: challenges,
      dimensions,
      // Vorher stand hier hart 'NORMAL'. Jedes erneute Betreten der
      // Einstellungen hat damit eine gespeicherte Haerte ueberschrieben.
      selection_difficulty:
        parsed_career_analyse.override_defaults?.selection_difficulty ??
        parsed_career_analyse.selection_difficulty ??
        'NORMAL',
      mandatory_criteria: mandatoryCriterions
    }

    if (status === 'WAIT_CONFIG') {
      meta.expert_mode_used = true
      // Stand beim Betreten, Referenz fuer das changed-Feld im Tracking.
      // Liegt in customer_dashboard_meta, weil synthesizeAnalyze
      // career_analyse vollstaendig ersetzt und dieses Feld nicht anfasst.
      meta.expert_mode_baseline = updatedOverrideDefaults
      parsed_career_analyse.override_defaults = updatedOverrideDefaults
    }

    if (status === 'WAIT_ACTIVE') {
      meta.expert_mode_used = false
      parsed_career_analyse.dimensions = dimensions
    }

    const input = {
      challenge_config_id: challenge_config_id || currentChallengeConfigId,
      app_settings: getAppSettings(app_settings, challenges),
      career_analyse: JSON.stringify(parsed_career_analyse),
      customer_dashboard_meta: JSON.stringify(meta)
    }

    handleNextNavigation({ input, nextStatus: status })
  }

  return (
    <>
      <div>
        <h1 className='text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl'>
          {t(I18N_PREFIX + 'title')}
        </h1>
        <ul className='ml-6 mt-3 max-w-xl list-disc'>
          {t(I18N_PREFIX + 'description').map((entry, index) => (
            <li key={index} className='mb-1.5 text-base text-gray-600'>
              {entry}
            </li>
          ))}
        </ul>
      </div>
      <div className='mt-6 flex flex-col gap-y-3 sm:flex-row sm:items-center sm:justify-between sm:gap-y-0'>
        <CompactStats items={stats} />
        <Button.SoftLG
          onClick={() => {
            trackEvent(
              trackEvents.CREATE_CAREER_TESTBATTERY_EXPERT_MODE_STARTED
            )
            setIsLoadingExpertMode(true)
            handleNext({ status: 'WAIT_CONFIG' })
          }}
          icon={<AdjustmentsHorizontalIcon className='h-4 w-4' />}
          text={t(I18N_PREFIX + 'expertmode_action')}
          isLoading={isLoadingExpertMode}
        />
      </div>
      <div className='mt-8 grid grid-cols-1 gap-4 xl:grid-cols-2'>
        {testbattery
          .map((exam_id) => ({
            exam_id,
            ...system.challenges[exam_id],
            ...configuration[exam_id]
          }))
          .map((challenge) => (
            <ChallengeCard
              key={challenge.exam_id}
              {...{
                challenge,
                className: 'h-full col-span-1 w-full max-w-xl mx-auto'
              }}
            />
          ))}
      </div>

      {!!Object.keys(moreChallenges).length && (
        <DividerWithText
          className='mt-8'
          text={t(I18N_PREFIX + 'more_challenges_title')}
        />
      )}

      <div className='mt-8 grid grid-cols-1 gap-4 xl:grid-cols-2'>
        {Object.keys(moreChallenges)
          .map((exam_id) => ({
            exam_id,
            ...system.challenges[exam_id],
            ...configuration[exam_id]
          }))
          .map((challenge) => {
            const { exam_id } = challenge
            const selected = moreChallenges[exam_id] === 'selected'

            const onClick = () => {
              const next =
                moreChallenges[exam_id] === 'selected'
                  ? 'not_selected'
                  : 'selected'
              setMoreChallenges({ [exam_id]: next })
              trackEvent(
                next === 'selected'
                  ? trackEvents.CREATE_CAREER_TESTBATTERY_OPTIONAL_CHALLENGE_ADDED
                  : trackEvents.CREATE_CAREER_TESTBATTERY_OPTIONAL_CHALLENGE_REMOVED,
                { exam_id }
              )
            }

            return (
              <ChallengeCard
                key={exam_id}
                {...{
                  challenge,
                  onClick,
                  selected,
                  // Hover und Auswahl stylt die Karte selbst, hier bleibt nur
                  // das Raster. Vorher lagen dieselben Klassen doppelt vor.
                  className: 'h-full col-span-1 w-full max-w-xl mx-auto'
                }}
              />
            )
          })}
      </div>

      <CreateCareerFooter>
        <div className='flex justify-end gap-x-2'>
          <Button.SecondaryXL
            onClick={() => handleBackNavigation({})}
            isLoading={isLoadingBack}
            text={t('create_career.back_action')}
          />
          <Button.PrimaryXL
            text={t('create_career.next_action')}
            isLoading={!isLoadingExpertMode && isLoadingNext}
            className='bg-green-600 hover:bg-green-500'
            onClick={() => handleNext({ status: 'WAIT_ACTIVE' })}
          />
        </div>
      </CreateCareerFooter>
    </>
  )
}

ConfirmTestbattery.propTypes = {
  career: PropTypes.object.isRequired,
  careerMutation: PropTypes.object.isRequired
}

export default ConfirmTestbattery
